import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { useSearchParams } from "react-router-dom";
import { Flex } from "@radix-ui/themes";
import type { DateRange } from "@/components/shared/date-range-picker-v2";
import {
  ExploreInteractionRow,
  ExploreInteractionType,
} from "@/v2/entities/explore/Explore.model";
import { Filter, mergeFilter } from "@/v2/entities/explore/Explore.filter";
import {
  ExploreAggColumnType,
  ExploreColumnType,
  ExploreGroupByColumnType,
} from "@/v2/entities/explore/columns/ExploreColum.model";
import { parseFiltersToFilterRequest } from "../explore-filter-menu/explore-filter.utils";
import {
  useExploreAggInteractions,
  useExploreInteractions,
} from "@/v2/entities/explore/Explore.api";
import { useExploreColumns } from "@/v2/entities/explore/columns/useExploreColumns";
import { Table } from "@/v2/components/table/Table";
import { SimulatorWrapper } from "@/routes/simulators/wrapper";
import { TicketView } from "../interaction-view/TicketView";
import { useExploreState } from "./useExploreState";
import { useStickySelectedInteraction } from "./useStickySelectedInteraction";
import { useExploreEnrichments } from "./useExploreEnrichments";
import { ConversationView } from "../interaction-view/ConversationView";
import { ExploreChartWizard } from "./ExploreMainChart";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import { ExploreHeader } from "./ExploreHeader";
import ExploreExport from "./ExploreExport";
import { useAuth0 } from "@auth0/auth0-react";
import { AllowedAggOrderBy } from "@/v2/features/explore/explore-display-options/ExploreDisplayOptions.model";
import { AllowedInteractionOrderBy } from "@/v2/features/explore/explore-display-options/ExploreDisplayOptions.model";
import { useExploreCustomFields } from "./useExploreCustomFields";
import { useExploreConversationCustomFieldsForView } from "./useExploreConversationCustomFieldsForView";
import { AskAnythingPanel } from "./ask-anything/AskAnythingPanel";

type ParsedDateRange = {
  dateFrom: string;
  dateTo: string;
};

function getUtcStartOfDayIso(dateString: string) {
  const date = new Date(dateString);

  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  ).toISOString();
}

function getUtcStartOfNextDayIso(dateString: string) {
  const date = new Date(dateString);

  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate() + 1)
  ).toISOString();
}

function buildCreatedAtDateRangeFilter(
  dateRange: DateRange,
  parsedDateRange: ParsedDateRange
): Filter {
  if (dateRange.type === "custom") {
    return {
      and: [
        { createdAt: { gte: getUtcStartOfDayIso(dateRange.dateFrom) } },
        { createdAt: { lt: getUtcStartOfNextDayIso(dateRange.dateTo) } },
      ],
    };
  }

  return {
    and: [
      { createdAt: { gte: parsedDateRange.dateFrom } },
      { createdAt: { lte: parsedDateRange.dateTo } },
    ],
  };
}

interface ExploreProps {
  source?: ExploreInteractionType;
  visibleColumns?: ExploreColumnType[];
  visibleAggColumns?: ExploreAggColumnType[];
  isEvaluated?: boolean;
  allowedGroupByOptions?: ExploreGroupByColumnType[];
  notAllowedGroupByOptions?: ExploreGroupByColumnType[];
}

function Explore({
  source = "tickets",
  visibleColumns,
  visibleAggColumns,
  isEvaluated = false,
  allowedGroupByOptions = [],
  notAllowedGroupByOptions = source === "conversations"
    ? [
        "agentAssigned",
        "day",
        "week",
        "month",
        "team",
        "status",
        "channel",
        "avgQaScore",
        "avgSentiment",
        "tenantId",
        "escalationReason",
        "skipReason",
        "resolvedReason",
        "scoreTitle",
        "scoreContent",
      ]
    : [
        "day",
        "week",
        "month",
        "status",
        "channel",
        "avgQaScore",
        "avgQuackMessageScoreRoundRange",
        "avgSentiment",
        "tenantId",
        "escalationReason",
        "skipReason",
        "resolvedReason",
        "originUrl",
        "scoreTitle",
        "scoreContent",
      ],
}: ExploreProps) {
  const { user } = useAuth0();
  const queryClient = useQueryClient();
  const hasWidgets = useFlag("explore-widget", user?.owner === "quack");

  //Add avgQaScoreRoundRange to notAllowedGroupByOptions if the feature flag is disabled
  const calculatedNotAllowedGroupByOptions: ExploreGroupByColumnType[] =
    useMemo(() => {
      return notAllowedGroupByOptions;
    }, [notAllowedGroupByOptions]);

  const exploreState = useExploreState({
    allowedGroupByOptions,
    notAllowedGroupByOptions: calculatedNotAllowedGroupByOptions,
  });

  const { state, handleGroupClick, handleSortingChange } = exploreState;
  const { groupBy, filters, sorting, parsedDateRange, dateRange } = state;

  const enrichments = useExploreEnrichments();
  const customFields = useExploreCustomFields();
  const conversationCustomFieldsForView =
    useExploreConversationCustomFieldsForView();

  const [viewMode, setViewMode] = useState<"both" | "table" | "charts">("both");
  const [search, setSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedInteractionId =
    searchParams.get("interactionId") ?? undefined;
  const setSelectedInteractionId = useCallback(
    (id: string | undefined) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id == null) {
            next.delete("interactionId");
          } else {
            next.set("interactionId", id);
          }
          return next;
        },
        { replace: false }
      );
    },
    [setSearchParams]
  );
  const [isAskAnythingOpen, setIsAskAnythingOpen] = useState(false);

  const searchFilter = useMemo<Filter | undefined>(() => {
    if (search) {
      const filter: Filter = {
        or: [
          { subject: { like: `%${search}%` } },
          { displayId: { like: `%${search}%` } },
          { tags: { contains: search } },
          { tenantUser: { like: `%${search}%` } },
        ],
      };

      return filter;
    }
  }, [search, user?.owner]);

  const filter = useMemo<Filter>(() => {
    let filter = buildCreatedAtDateRangeFilter(dateRange, parsedDateRange);

    if (Object.keys(filters).length > 0) {
      const filterToMerge = parseFiltersToFilterRequest(filters);
      filter = mergeFilter(filter, filterToMerge);
    }

    if (searchFilter != null) {
      filter = mergeFilter(filter, searchFilter);
    }

    return filter;
  }, [dateRange, parsedDateRange, filters, searchFilter]);

  const hiddenFilters = useMemo(() => {
    if (isEvaluated) {
      return mergeFilter(filter, {
        and: [{ avgQaScore: { gte: 0 } }],
      });
    }

    if (source === "conversations") {
      return mergeFilter(filter, {
        and: [{ subject: { isnotnull: "true" } }],
      });
    }

    return filter;
  }, [isEvaluated, filter, source]);

  const { columns, aggColumns } = useExploreColumns({
    visibleColumns,
    visibleAggColumns,
    source,
    groupBy,
    enrichments: source === "tickets" ? enrichments : [],
    customFields:
      source === "tickets"
        ? customFields
        : source === "conversations"
        ? conversationCustomFieldsForView
        : [],
  });

  const exploreInteractionsQuery = useExploreInteractions({
    source,
    orderBy: sorting?.[0]?.id as AllowedInteractionOrderBy | AllowedAggOrderBy,
    orderDir: sorting?.[0]?.desc ? "desc" : "asc",
    filter: hiddenFilters,
    enabled: groupBy == null,
  });

  const exploreAggInteractionsQuery = useExploreAggInteractions({
    source,
    groupBy,
    orderBy: sorting?.[0]?.id as AllowedInteractionOrderBy | AllowedAggOrderBy,
    orderDir: sorting?.[0]?.desc ? "desc" : "asc",
    filter: hiddenFilters,
    enabled: groupBy != null,
  });

  const selectedInteraction = useStickySelectedInteraction(
    selectedInteractionId,
    exploreInteractionsQuery.data
  );

  const handleInteractionClick = useCallback(
    (row: ExploreInteractionRow) => {
      setSelectedInteractionId(row.displayId);
    },
    [setSelectedInteractionId]
  );

  const handleCloseInteraction = useCallback(() => {
    setSelectedInteractionId(undefined);
  }, [setSelectedInteractionId]);

  const handleInteractionValidated = useCallback(() => {
    void queryClient.invalidateQueries(["explore-v2"]);
  }, [queryClient]);

  const handleFetchNextInteractions = useCallback(async () => {
    await exploreInteractionsQuery.fetchNextPage();
  }, [exploreInteractionsQuery]);

  return (
    <Flex width="100%" height="100dvh" overflow="hidden">
      <Flex
        width="100%"
        height="100%"
        direction="column"
        className="bg-background"
        overflow="hidden"
        style={{ flex: 1, minWidth: 0 }}
      >
        <Flex
          width={"100%"}
          maxHeight={"100dvh"}
          height={"100%"}
          direction="column"
          className="bg-background"
          overflow={"scroll"}
        >
          <ExploreHeader
            {...exploreState}
            viewMode={viewMode}
            source={source}
            filter={hiddenFilters}
            search={search}
            isEvaluated={isEvaluated}
            allowedGroupByOptions={allowedGroupByOptions}
            notAllowedGroupByOptions={calculatedNotAllowedGroupByOptions}
            conversationCustomFields={
              source === "conversations"
                ? conversationCustomFieldsForView
                : undefined
            }
            ticketCustomFields={source === "tickets" ? customFields : undefined}
            setViewMode={setViewMode}
            setSearch={setSearch}
            isAskAnythingOpen={isAskAnythingOpen}
            setIsAskAnythingOpen={setIsAskAnythingOpen}
          >
            <ExploreExport
              source={source}
              groupBy={groupBy}
              columns={columns}
              aggColumns={aggColumns}
              exploreInteractions={exploreInteractionsQuery.data}
              exploreAggInteractions={exploreAggInteractionsQuery.data?.results}
            />
          </ExploreHeader>

          <Flex flexShrink={"0"} className="h-[102px]" />

          {hasWidgets && (viewMode === "both" || viewMode === "charts") && (
            <Flex flexShrink={"0"} direction={"column"}>
              <ExploreChartWizard
                hiddenFilters={hiddenFilters}
                groupBy={groupBy}
                source={source}
              />
            </Flex>
          )}

          <Flex
            minHeight={"calc(100dvh - 102px)"}
            px={"3"}
            py={"1"}
            direction={"column"}
          >
            {(viewMode === "both" || viewMode === "table") && (
              <>
                {groupBy == null && (
                  <Table
                    data={exploreInteractionsQuery.data}
                    columns={columns}
                    loading={exploreInteractionsQuery.isLoading}
                    sorting={sorting}
                    onSortingChange={handleSortingChange}
                    hasNextPage={exploreInteractionsQuery.hasNextPage}
                    isFetching={exploreInteractionsQuery.isFetching}
                    isFetchingNextPage={
                      exploreInteractionsQuery.isFetchingNextPage
                    }
                    fetchNextPage={handleFetchNextInteractions}
                    onRowClicked={handleInteractionClick}
                  />
                )}

                {groupBy != null && (
                  <Table
                    data={exploreAggInteractionsQuery.data?.results ?? []}
                    columns={aggColumns}
                    loading={exploreAggInteractionsQuery.isLoading}
                    sorting={sorting}
                    onSortingChange={handleSortingChange}
                    onRowClicked={handleGroupClick}
                  />
                )}
              </>
            )}
          </Flex>
        </Flex>
      </Flex>

      <AskAnythingPanel
        isOpen={isAskAnythingOpen}
        onOpenChange={setIsAskAnythingOpen}
        source={source}
        filter={hiddenFilters}
        dateRange={state.dateRange}
        filters={state.filters}
      />

      {selectedInteraction != null && (
        <>
          {source === "tickets" && (
            <SimulatorWrapper>
              <TicketView
                key={selectedInteraction.displayId}
                ticket={selectedInteraction}
                enrichments={source === "tickets" ? enrichments : []}
                customFields={source === "tickets" ? customFields : []}
                allowValidate
                onTicketValidated={handleInteractionValidated}
                onClose={handleCloseInteraction}
              />
            </SimulatorWrapper>
          )}

          {source === "conversations" && (
            <ConversationView
              key={selectedInteraction.displayId}
              conversation={selectedInteraction}
              enrichments={source === "conversations" ? enrichments : []}
              customFields={
                source === "conversations"
                  ? conversationCustomFieldsForView
                  : []
              }
              allowValidate
              onConversationValidated={handleInteractionValidated}
              onClose={handleCloseInteraction}
            />
          )}
        </>
      )}
    </Flex>
  );
}

export default Explore;
